import {
  Text,
  InputSelectControlField,
  CollapsibleSection,
  TextInputField,
  Flex
} from '@sequencehq/core-components'
import {
  CountriesAlpha2,
  getStateOptions
} from '@sequencehq/api/utils/commonEnums'
import { getPostcodeLabel } from '@sequencehq/utils'
import { useMemo } from 'react'
import { CountrySelectField } from 'SettingsV2/index'
import { CustomerFormFieldControls } from '../types'
import { useSectionExpansion } from '../useSectionExpansion'
import { fieldStyles, inputStyles } from './styles'

export type BillingInformationSectionProps = {
  /** The address field controls — pass the hook's `fields` directly. */
  addressFields: Pick<
    CustomerFormFieldControls,
    | 'address.country'
    | 'address.line1'
    | 'address.line2'
    | 'address.town'
    | 'address.state'
    | 'address.postcode'
  >
  'data-testid'?: string
}

export const BillingInformationSection = ({
  addressFields,
  'data-testid': testId
}: BillingInformationSectionProps) => {
  const selectedCountry = addressFields['address.country']
    .value as CountriesAlpha2

  const addressStateOptions = useMemo(() => {
    if (!selectedCountry) return []
    return getStateOptions(selectedCountry)
  }, [selectedCountry])

  const showAddressState = addressStateOptions.length > 0

  const { isOpen, onToggle } = useSectionExpansion(
    Object.values(addressFields).some(
      field => field.validationErrors.length > 0
    )
  )

  return (
    <CollapsibleSection
      title="Billing information"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <Flex direction="column" gap="8px">
        <Text {...fieldStyles.label}>Billing details</Text>
        <CountrySelectField
          data-testid={testId ? `${testId}.address.country` : undefined}
          value={addressFields['address.country'].value}
          onChange={newCountryValue =>
            addressFields['address.country'].onChange(newCountryValue)
          }
          validationErrors={addressFields['address.country'].validationErrors}
          styles={fieldStyles}
        />

        {addressFields['address.country'].value && (
          <>
            <TextInputField
              data-testid={testId ? `${testId}.address.line1` : undefined}
              value={addressFields['address.line1'].value}
              onChange={e =>
                addressFields['address.line1'].onChange(e.target.value)
              }
              placeholder="Address line 1"
              validationErrors={addressFields['address.line1'].validationErrors}
              {...inputStyles}
              styles={fieldStyles}
            />

            <TextInputField
              data-testid={testId ? `${testId}.address.line2` : undefined}
              value={addressFields['address.line2'].value}
              onChange={e =>
                addressFields['address.line2'].onChange(e.target.value)
              }
              placeholder="Address line 2 (optional)"
              validationErrors={[]}
              {...inputStyles}
              styles={fieldStyles}
            />

            <TextInputField
              data-testid={testId ? `${testId}.address.town` : undefined}
              value={addressFields['address.town'].value}
              onChange={e =>
                addressFields['address.town'].onChange(e.target.value)
              }
              placeholder="City/town"
              validationErrors={addressFields['address.town'].validationErrors}
              {...inputStyles}
              styles={fieldStyles}
            />

            {showAddressState && (
              <InputSelectControlField
                data-testid={testId ? `${testId}.address.state` : undefined}
                value={addressFields['address.state'].value}
                onChange={newStateValue =>
                  addressFields['address.state'].onChange(
                    newStateValue as never
                  )
                }
                options={addressStateOptions}
                placeholder="Select state"
                validationErrors={
                  addressFields['address.state'].validationErrors
                }
                styles={fieldStyles}
              />
            )}

            <TextInputField
              data-testid={testId ? `${testId}.address.postcode` : undefined}
              value={addressFields['address.postcode'].value}
              placeholder={getPostcodeLabel(
                addressFields['address.country'].value
              )}
              onChange={e =>
                addressFields['address.postcode'].onChange(e.target.value)
              }
              validationErrors={
                addressFields['address.postcode'].validationErrors
              }
              {...inputStyles}
              styles={fieldStyles}
            />
          </>
        )}
      </Flex>
    </CollapsibleSection>
  )
}
